/**
 * useImpression — fire a callback the first time an element is meaningfully
 * visible in the viewport, then stop watching.
 * ===========================================================================
 * Used for widget "view" analytics: a widget counts as seen once at least
 * `threshold` of it has scrolled into view. We fire ONCE (impressions aren't
 * re-counted when the user scrolls back) and disconnect the observer.
 *
 *     const ref = useImpression<HTMLDivElement>(() =>
 *       logEvent({ section: item.type, action: EventAction.VIEW })
 *     )
 *     return <div ref={ref}>…</div>
 *
 * `onImpression` is captured in a ref so callers can pass an inline closure
 * without re-arming the observer on every render.
 */
import { useCallback, useEffect, useRef } from 'react'

export function useImpression<T extends Element>(
  onImpression: () => void,
  // threshold 0 = fire as soon as ANY part scrolls in. Don't raise this: a
  // ratio like 0.5 can never be met by a widget taller than the viewport, so
  // its impression would be silently lost on small screens.
  threshold = 0
): (node: T | null) => void {
  const firedRef = useRef(false)
  const callbackRef = useRef(onImpression)
  callbackRef.current = onImpression

  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    return () => observerRef.current?.disconnect()
  }, [])

  // Callback ref: (re)attach the observer whenever the observed node changes.
  return useCallback(
    (node: T | null) => {
      observerRef.current?.disconnect()
      if (!node || firedRef.current) return
      if (typeof IntersectionObserver === 'undefined') {
        // No observer (old WebView / SSR): count it as seen so we don't lose
        // the impression entirely.
        firedRef.current = true
        callbackRef.current()
        return
      }
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting) && !firedRef.current) {
            firedRef.current = true
            callbackRef.current()
            observerRef.current?.disconnect()
          }
        },
        { threshold }
      )
      observerRef.current.observe(node)
    },
    [threshold]
  )
}
